<template>
  <div>
    <div v-for="modifier in modifiers" :key="modifier.modifierId" class="modifier">
      <div class="d-flex modifier__container" :class="{ modifier__instruction: hasInstruction(modifier) }">
        <div class="modifier__data d-flex flex-space-between">
          <component
            :is="multipleChoice ? 'app-checkbox' : 'app-radio-button'"
            v-model="selectedModifiers"
            :group-name="modifierGroup.id"
            :payload="modifier.modifierId"
            :disabled="isDisabled(modifier.modifierId)"
            class="modifier__expanded-item"
            :class="{
              'modifier__expanded-item__radios': !multipleChoice,
              'modifier__expanded-item__no-arrow': !modifier.modifierGroups?.length
            }"
            @update:modelValue="onUpdateSelection(modifier)"
          >
            <modifier-label
              class="modifier__label"
              :modifier="modifier"
              :is-selected="isSelected(modifier.modifierId)"
            ></modifier-label>
            <span
              class="modifier__description"
              :class="{
                modifier__active: isSelected(modifier.modifierId),
                modifier__inactive: isDisabled(modifier.modifierId)
              }"
              >{{ getModPrice(modifier) }}
            </span>
          </component>
        </div>
        <div v-if="hasInstruction(modifier) && isSelected(modifier.modifierId)" class="d-flex m-b-5 m-l-16">
          <div v-for="modifierGroup in modifier.modifierGroups" :key="modifierGroup.id" class="d-flex flex-wrap">
            <modifier-chip
              v-for="mod in modifierGroup.modifiers"
              :key="mod.modifierId"
              :modifier-id="String(mod.modifierId)"
              :text="mod.name"
              :selected="modifierGroup.selectedModifiers?.includes(mod.modifierId)"
              :disabled="!modifierGroup.selectedModifiers?.includes(mod.modifierId) && maxInstructionsSelected(modifierGroup)"
              @update="(e, modifierId) => instructionSelected(modifierGroup, modifierId)"
            ></modifier-chip>
          </div>
        </div>
        <div
          v-if="enableEditArrow(modifier) && !hasInstruction(modifier)"
          class="modifier__arrow_container d-flex"
          :class="{
            modifier__inactive: isDisabled(modifier.modifierId),
            modifier__arrow_container__arrow: !isDisabled(modifier.modifierId)
          }"
          @click="enableEditArrow(modifier) && editArrowClicked(modifier)"
        >
          <img src="@/assets/images/link-grey.svg" />
        </div>
      </div>
      <div class="modifier__edit" @click="enableEditArrow(modifier) && editArrowClicked(modifier)">
        <div v-if="!isValidModifier(modifier)" class="modifier__error">Select Options</div>
        <cart-list-item-modifiers-tree v-if="!hasInstruction(modifier)" class="modifier__children" :item="modifier" type="modal">
        </cart-list-item-modifiers-tree>
      </div>
    </div>
  </div>
</template>

<script>
import { PRICING_MODE } from "@/../app-constants";
import ModifierLabel from "./ModifierLabel";
import { prependUsdSign, addition } from "@/utils";
import ModifierChip from "./ModifierChip.vue";
import CartListItemModifiersTree from "../../../../../routes/shopping-cart/components/cart-item/cart-list-item-modifiers-tree/CartListItemModifiersTree";

export default {
  name: "ModifiersSelector",
  components: { ModifierLabel, ModifierChip, CartListItemModifiersTree },
  props: {
    multipleChoice: Boolean,
    value: Array,
    modifierGroup: Object,
    maxLimit: Number,
    modifiers: Array,
    selectedMods: [Array, String],
    parentModifier: String
  },
  emits: ["onUpdateSelection"],
  data() {
    return {
      pricingModes: PRICING_MODE,
      selectedModifiers: this.selectedMods
    };
  },
  computed: {
    selectedModifiersArray() {
      if (Array.isArray(this.selectedModifiers)) {
        return this.selectedModifiers;
      } else {
        return this.selectedModifiers ? [this.selectedModifiers] : [];
      }
    }
  },
  updated() {
    this.selectedModifiers = this.selectedMods;
  },
  methods: {
    getModPrice(modifier) {
      if (parseFloat(modifier.cost) === 0 || modifier.rules.pricingMode === this.pricingModes.includedInParentPrice) {
        return "";
      }

      return addition(prependUsdSign(modifier.cost));
    },
    isSelected(id) {
      return this.selectedModifiersArray.some((itemId) => itemId + "" === id + "");
    },
    isDisabled(modifierId) {
      if (!this.multipleChoice) return false;
      if (this.selectedModifiersArray.length >= this.maxLimit && !this.isSelected(modifierId)) return true;
      return false;
    },
    isValid() {
      return this.modifiers.every((modifier) => this.isValidModifier(modifier));
    },
    isValidModifier(modifier) {
      const validModifierGroups = (modifierGroups) => {
        const isValidGroup = this.checkModifierGroups(modifierGroups);
        if (!isValidGroup) return false;

        const nestedGroups = modifierGroups
          .map((g) => g.modifiers)
          ?.filter((mod) => this.isSelected(mod.modifierId))
          .map((modifier) => modifier.modifierGroups);

        if (nestedGroups?.length === 0) {
          return isValidGroup;
        }
        validModifierGroups(nestedGroups);
      };
      return (
        !this.isSelected(modifier.modifierId) ||
        !modifier.modifierGroups?.length > 0 ||
        validModifierGroups(modifier.modifierGroups)
      );
    },
    checkModifierGroups(groups) {
      return groups?.every((group) => {
        const { minModifiers, maxModifiers, selectedModifiers } = group;
        let selectedNestedModifierArray = selectedModifiers ? [selectedModifiers] : [];
        if (Array.isArray(selectedModifiers)) {
          selectedNestedModifierArray = selectedModifiers;
        }
        const modifierGroupCheck =
          selectedNestedModifierArray?.length >= minModifiers && selectedNestedModifierArray?.length <= maxModifiers;

        return modifierGroupCheck;
      });
    },
    cleanModifierTreeSelection(modifier) {
      modifier?.modifierGroups?.forEach((modifierGroup) => {
        this.$emit("onUpdateSelection", {
          id: modifierGroup.id,
          selectedModifiers: modifierGroup.multipleChoice ? [] : "",
          goToNextLevel: false,
          selectedParentModifier: null
        });
        modifierGroup?.modifiers?.forEach((mod) => {
          this.cleanModifierTreeSelection(mod);
        });
      });
    },
    cleanModifierGroupTreeSelection(modifierGroup) {
      modifierGroup?.modifiers?.forEach((modifier) => {
        modifier?.modifierGroups?.forEach((modGroup) => {
          this.$emit("onUpdateSelection", {
            id: modGroup.id,
            selectedModifiers: modGroup.multipleChoice ? [] : "",
            goToNextLevel: false,
            selectedParentModifier: null
          });
          this.cleanModifierGroupTreeSelection(modGroup);
        });
      });
    },
    onUpdateSelection(modifier) {
      if (!this.multipleChoice) {
        //Mod changed is radio button, need to unselect all modifierGroup's modifiers tree
        this.cleanModifierGroupTreeSelection(this.modifierGroup);
      } else if (!this.isSelected(modifier.modifierId)) {
        //mod changed is a checkbox being unselected need to unselect modifier tree
        this.cleanModifierTreeSelection(modifier);
      }
      //update modifiers status for current modifierGroup
      const goToNextLevel =
        this.isSelected(modifier.modifierId) && !!modifier.modifierGroups?.length && !this.hasInstruction(modifier);
      this.$emit("onUpdateSelection", {
        id: this.modifierGroup.id,
        selectedModifiers: this.selectedModifiers,
        goToNextLevel,
        selectedParentModifier: { modifierId: modifier.modifierId, name: modifier.name }
      });
    },
    instructionSelected(modifierGroup, modifierId) {
      const selectedModifiers = this.selectInstructions(modifierGroup, modifierId);

      this.$emit("onUpdateSelection", {
        id: modifierGroup.id,
        selectedModifiers: selectedModifiers,
        goToNextLevel: false,
        selectedParentModifier: null
      });
    },
    selectInstructions(modifierGroup, modifierId) {
      if (!modifierGroup.multipleChoice) {
        if (modifierGroup.selectedModifiers === modifierId) {
          return "";
        }
        return modifierId;
      }

      if (!modifierGroup.selectedModifiers.includes(modifierId)) {
        return [...modifierGroup.selectedModifiers, modifierId];
      }
      return modifierGroup.selectedModifiers.filter((modId) => modId !== modifierId);
    },
    editArrowClicked(modifier) {
      //single modifier selection
      if (!this.multipleChoice) {
        if (!this.isSelected(modifier.modifierId)) {
          //New mod selected, clean up modifierGroup selection and select new mod
          this.cleanModifierTreeSelection(modifier);
          this.selectedModifiers = modifier.modifierId;
        }
      }

      //multiple modifier selection
      if (!this.isSelected(modifier.modifierId)) {
        //New mod selected select it
        this.selectedModifiers.push(modifier.modifierId);
      }

      //always update mods in parent and go to next level
      this.$emit("onUpdateSelection", {
        id: this.modifierGroup.id,
        selectedModifiers: this.selectedModifiers,
        goToNextLevel: true,
        selectedParentModifier: { modifierId: modifier.modifierId, name: modifier.name }
      });
    },
    enableEditArrow(modifier) {
      return modifier.modifierGroups?.length && !this.isDisabled(modifier.modifierId);
    },
    hasInstruction(modifier) {
      return (modifier.modifierGroups && modifier.modifierGroups[0]?.isInstruction) || false;
    },
    maxInstructionsSelected(modifierGroup) {
      if (!modifierGroup.multipleChoice) return false;
      return modifierGroup.selectedModifiers.length >= modifierGroup.maxModifiers;
    }
  }
};
</script>

<style lang="scss" scoped>
.modifier {
  &__description {
    display: flex;
    align-items: center;
    min-width: 60px;
    color: $group-3-color-3;
  }

  &__data {
    flex: 1;
  }

  &__label {
    margin-left: 38px;
  }

  &__active {
    font-weight: bold;
    color: $group-3-color-2;
  }

  &__inactive {
    opacity: 0.4;
  }

  &__arrow_container {
    justify-content: center;
    flex: 0 0 0px;
    padding: 17px 0 17px 0;

    &__arrow {
      cursor: pointer;
    }
  }

  &__container {
    align-items: center;
  }

  &__error {
    font-size: 14px;
    padding: 10px 10px 10px 38px;
    margin-bottom: 12px;
    border-radius: 3px;
    color: $error-color;
    background-color: rgba(141, 154, 174, 0.08);
  }

  &__children {
    padding: 14px 10px 4px;
    margin-bottom: 12px;
    border-radius: 7px;
    background-color: rgba(141, 154, 174, 0.08);
  }

  &__expanded-item {
    flex-grow: 1;
    padding: 17px 0 17px;
    justify-content: space-between;

    &__no-arrow {
      margin-right: 4px;
    }

    &__radios {
      padding-right: 12px;
    }
  }

  &__instruction {
    flex-direction: column;
    align-items: initial;

    .modifier__expanded-item {
      padding-right: 17px;
    }
  }
}
</style>
